import type { ReactElement } from "react";
import { useState, useMemo } from "react";

import { translate } from "app/filters/i18nFilter";
import type { IRadioSelectItem } from "app/pages/sales-intelligence/common-components/form-fields";
import { TopLevelDomainOption } from "app/pages/sales-intelligence/domain/leads/types/enums";
import type { IFilterViewBaseProps } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filterInstance";
import InteractiveTextInput from "app/pages/sales-intelligence/sub-modules/advanced-search/components/shared/InteractiveTextInput/InteractiveTextInput";

import type FindTopLevelDomainsFilter from "../../../models/TopLevelDomainsFilter/TopLevelDomainsFilter";
import { FilterContentContainer, StyledRadioSelect } from "../styles";

import { TRANSLATION_PREFIX } from "./const";
import { StyledInputTip } from "./styles";
import { parseDomainExtensions } from "./utils/processTopLevelDomainList";

const TopLevelDomainsFilter = ({
    filter,
    value,
    onFilterUpdate,
}: IFilterViewBaseProps<FindTopLevelDomainsFilter>): ReactElement | null => {
    const [topLevelDomainOption, setTopLevelDomainOption] = useState<TopLevelDomainOption>(
        value?.type ?? TopLevelDomainOption.Contains,
    );

    const handleRadioSelect = (type: TopLevelDomainOption) => {
        setTopLevelDomainOption(type);
        if (value?.values?.length) {
            onFilterUpdate({ ...value, type });
        }
    };

    const handleDomainExtensionsAdd = (input: string) => {
        const currentValues = value?.values ?? [];
        const parsedExtensions = parseDomainExtensions(input);

        const set = new Set(parsedExtensions);
        currentValues.forEach((v) => set.delete(v));

        const uniqueList = [...set];

        if (uniqueList.length) {
            onFilterUpdate({
                type: topLevelDomainOption,
                values: currentValues.concat(uniqueList),
            });
        }
    };

    const radioItems = useMemo(
        (): IRadioSelectItem[] =>
            filter.options.map((val) => ({
                id: val,
                text: translate(`${TRANSLATION_PREFIX}.type.${val}`),
            })),
        [filter.options],
    );

    return (
        <FilterContentContainer>
            <StyledRadioSelect
                size="sm"
                selected={topLevelDomainOption}
                onSelect={handleRadioSelect}
                items={radioItems}
            />
            <div>
                <InteractiveTextInput
                    iconName="globe"
                    onAdd={handleDomainExtensionsAdd}
                    placeholder={translate(`${TRANSLATION_PREFIX}.input.placeholder`)}
                />
                <StyledInputTip>{translate(`${TRANSLATION_PREFIX}.input_tip.${topLevelDomainOption}`)}</StyledInputTip>
            </div>
        </FilterContentContainer>
    );
};

export default TopLevelDomainsFilter;
